import React, { useMemo } from 'react';
import { ArrowLeftIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AvailabilityForm } from '../../components';
import {
  useAvailabilityQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
} from '../../data';
import type { AvailabilityDocument, AvailabilityFormValues } from '../../models';
import { AvailabilityStatuses } from '../../models';
import { useToast } from '@/hooks/useToast';
import { deleteAvailabilityById } from '../../services/availabilityService';

function mapAvailabilityToFormValues(availability: AvailabilityDocument): AvailabilityFormValues {
  return {
    clientId: availability.clientId,
    clientName: availability.clientName,
    clientLogo: availability.clientLogo ?? null,
    initiative: availability.initiative,
    status: availability.status,
    needType: availability.needType,
    referenceDate: availability.referenceDate?.toDate?.() ?? null,
    notes: availability.notes ?? '',
    isInternalDemand: availability.isInternalDemand ?? false,
    priorityLevel:
      typeof availability.priorityLevel === 'number' ? availability.priorityLevel : null,
    slots: availability.slots.map((slot) => ({
      slotId: slot.slotId,
      title: slot.title ?? '',
      bucketType: slot.bucketType,
      bucketValue: Number(slot.bucketValue ?? 0),
      capabilities: slot.capabilities.map((capability) => ({ ...capability })),
      workModel: slot.workModel,
      workCity: slot.workCity ?? null,
      workState: slot.workState ?? null,
      acceptsPJ: slot.acceptsPJ,
      cltSalaryCeiling: slot.cltSalaryCeiling ?? null,
      candidates: slot.candidates ?? [],
      chosenCollaboratorId: slot.chosenCollaboratorId ?? null,
      lastMatchFilters: slot.lastMatchFilters ?? null,
      lastMatchResultsCount:
        typeof slot.lastMatchResultsCount === 'number' && Number.isFinite(slot.lastMatchResultsCount)
          ? slot.lastMatchResultsCount
          : null,
      lastSuggestionAt: slot.lastSuggestionAt?.toDate?.() ?? null,
      fulfillmentStrategy: slot.fulfillmentStrategy ?? 'known',
    })),
  };
}

export default function CollaboratorAvailabilityFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error, warning } = useToast();
  const createAvailabilityMutation = useCreateAvailabilityMutation();
  const updateAvailabilityMutation = useUpdateAvailabilityMutation();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isCancelling, setIsCancelling] = React.useState(false);

  const availabilityId = searchParams.get('availabilityId');
  const isEditing = Boolean(availabilityId);

  const {
    data: availabilityData,
    isLoading: isLoadingAvailability,
    isError: isAvailabilityError,
    error: availabilityError,
  } = useAvailabilityQuery({
    availabilityId: availabilityId ?? undefined,
    enabled: isEditing,
  });

  const initialValues = useMemo(() => {
    if (!isEditing || !availabilityData) {
      return null;
    }
    return mapAvailabilityToFormValues(availabilityData);
  }, [availabilityData, isEditing]);

  const isSubmitting = createAvailabilityMutation.isLoading || updateAvailabilityMutation.isLoading;

  const handleBack = () => {
    navigate('/collaborators/availabilities');
  };

  const handleDeleteAvailability = async () => {
    if (!availabilityId) {
      warning('Operação indisponível', 'Não foi possível identificar a disponibilidade.');
      return;
    }

    const confirmed = window.confirm('Deseja remover esta necessidade? Esta ação é irreversível.');
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAvailabilityById(availabilityId);
      success('Disponibilidade removida', 'A necessidade foi excluída com sucesso.');
      navigate('/collaborators/availabilities');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover disponibilidade.';
      error('Não foi possível remover a disponibilidade', message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelAvailability = async () => {
    if (!availabilityId || !availabilityData) {
      warning('Operação indisponível', 'Não foi possível identificar a disponibilidade.');
      return;
    }

    if (availabilityData.status === AvailabilityStatuses.Cancelled) {
      warning('Necessidade já cancelada', 'Esta disponibilidade já está com status cancelado.');
      return;
    }

    const confirmed = window.confirm(
      'Deseja cancelar esta necessidade? O status será atualizado para Cancelada.'
    );
    if (!confirmed) {
      return;
    }

    setIsCancelling(true);
    try {
      const payload = mapAvailabilityToFormValues(availabilityData);
      payload.status = AvailabilityStatuses.Cancelled;

      await updateAvailabilityMutation.mutateAsync({
        id: availabilityId,
        values: payload,
      });

      success('Disponibilidade cancelada', 'A necessidade foi marcada como cancelada.');
      navigate('/collaborators/availabilities');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao cancelar disponibilidade.';
      error('Não foi possível cancelar a disponibilidade', message);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmit = async (values: AvailabilityFormValues) => {
    try {
      if (isEditing && availabilityId) {
        await updateAvailabilityMutation.mutateAsync({
          id: availabilityId,
          values,
        });
        success('Disponibilidade atualizada', 'As alterações foram salvas com sucesso.');
      } else {
        await createAvailabilityMutation.mutateAsync(values);
        success('Disponibilidade criada', 'A necessidade foi registrada com sucesso.');
      }
      navigate('/collaborators/availabilities');
    } catch (err) {
      console.error('[availability-form] erro ao salvar disponibilidade', err);
      const message = err instanceof Error ? err.message : 'Erro ao salvar disponibilidade';
      error('Não foi possível salvar a disponibilidade', message);
      throw err;
    }
  };

  if (isEditing) {
    if (isLoadingAvailability) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Voltar para disponibilidades
            </button>
          </div>

          <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-6 text-sm text-indigo-700">
            Carregando dados da disponibilidade...
          </div>
        </div>
      );
    }

    if (isAvailabilityError || !availabilityData) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Voltar para disponibilidades
            </button>
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            Não foi possível carregar os dados desta disponibilidade.
            {availabilityError?.message ? (
              <span className="ml-1 font-medium">{availabilityError.message}</span>
            ) : null}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Voltar para disponibilidades
        </button>

        <div className="flex flex-wrap items-center gap-3">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleCancelAvailability}
                className="inline-flex items-center gap-2 rounded-md border border-amber-200 px-3 py-2 text-sm font-semibold text-amber-600 transition duration-150 hover:bg-amber-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isCancelling || isDeleting || isSubmitting}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                {isCancelling ? 'Cancelando...' : 'Cancelar necessidade'}
              </button>

              <button
                type="button"
                onClick={handleDeleteAvailability}
                className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition duration-150 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isDeleting || isCancelling || isSubmitting}
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                {isDeleting ? 'Removendo...' : 'Excluir necessidade'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <AvailabilityForm
        onSubmit={handleSubmit}
        onCancel={handleBack}
        isSubmitting={isSubmitting}
        initialValues={initialValues}
        availabilityId={availabilityId ?? undefined}
      />
    </div>
  );
}
